import { z } from 'zod';
/**
 * Действия, обязательные к записи в журнал (ТЗ §10.4).
 *
 * Список пополняется по фазам: здесь объявлены действия фазы 1 плюс те,
 * что §10.4 называет прямо, — они появятся в своих фазах, но код действия
 * фиксируется сразу, чтобы записи журнала не пришлось потом переименовывать.
 */
export const AuditAction = {
    // Фаза 1
    AUTH_LOGIN: 'auth.login',
    AUTH_LOGIN_FAILED: 'auth.login_failed',
    AUTH_LOGOUT: 'auth.logout',
    AUTH_REFRESH_REUSE: 'auth.refresh_reuse',
    AUTH_PASSWORD_CHANGED: 'auth.password_changed',
    USER_INVITED: 'user.invited',
    USER_UPDATED: 'user.updated',
    USER_DEACTIVATED: 'user.deactivated',
    USER_PASSWORD_RESET: 'user.password_reset',
    USER_ROLES_CHANGED: 'user.roles_changed',
    ORG_CREATED: 'org.created',
    ORG_UPDATED: 'org.updated',
    SITE_CREATED: 'site.created',
    SITE_UPDATED: 'site.updated',
    SITE_DELETED: 'site.deleted',
    // Фаза 3. Перемещение узлов по карте в журнал не пишется: это оформление,
    // а не изменение состава сети, и при активном редактировании такие записи
    // вытеснили бы из журнала всё содержательное.
    NODE_CREATED: 'node.created',
    NODE_UPDATED: 'node.updated',
    NODE_DELETED: 'node.deleted',
    NODE_ADDRESSES_REPLACED: 'node.addresses_replaced',
    MONITOR_CREATED: 'monitor.created',
    MONITOR_UPDATED: 'monitor.updated',
    MONITOR_DELETED: 'monitor.deleted',
    // Объявлены заранее, реализуются в своих фазах
    AGENT_REGISTERED: 'agent.registered',
    AGENT_UPDATED: 'agent.updated',
    AGENT_DELETED: 'agent.deleted',
    CREDENTIAL_CREATED: 'credential.create',
    CREDENTIAL_UPDATED: 'credential.update',
    CREDENTIAL_DELETED: 'credential.delete',
    VNC_CONNECT: 'vnc.connect',
    VNC_DISCONNECT: 'vnc.disconnect',
    FILE_UPLOAD: 'file.upload',
    FILE_DOWNLOAD: 'file.download',
    FILE_DELETE: 'file.delete',
    // Фаза 6. §10.4 требует журнала на «любую файловую операцию», а §8.2
    // предусматривает создание каталога; переименование добавлено вместе
    // с ним. Просмотр каталога сюда не входит намеренно — обоснование
    // в отчёте фазы 6.
    FILE_MKDIR: 'file.mkdir',
    FILE_RENAME: 'file.rename',
    // Фаза 7. Просмотр живого потока пишется один раз на открытие ячейки,
    // а не на каждое продление аренды: иначе видеостена 4×4 писала бы
    // шестнадцать записей каждые десять секунд и вытеснила бы журнал.
    CAMERA_CREATED: 'camera.created',
    CAMERA_UPDATED: 'camera.updated',
    CAMERA_DELETED: 'camera.deleted',
    CAMERA_VIEW: 'camera.view',
    CAMERA_ARCHIVE_VIEW: 'camera.archive_view',
    // Видеорегистраторы (ADR 037). Определение каналов пишется: оно заводит
    // и удаляет камеры на стене, и исчезнувший из раскладки канал ищут
    // именно по этой записи. Строки каналов отдельными записями не пишутся —
    // на шестнадцатиканальном регистраторе они вытеснили бы журнал.
    RECORDER_CREATED: 'recorder.created',
    RECORDER_UPDATED: 'recorder.updated',
    RECORDER_DELETED: 'recorder.deleted',
    RECORDER_CHANNELS_DETECTED: 'recorder.channels_detected',
    // Раскладки видеостены. Пишутся потому, что общая раскладка — предмет
    // совместного пользования: её правку и удаление заметит вся смена.
    LAYOUT_SAVED: 'layout.saved',
    LAYOUT_DELETED: 'layout.deleted',
    // Фаза 9. Канал уведомлений — это адрес, куда уходят имена узлов
    // и их состояние, а правило решает, что именно туда уходит. И то,
    // и другое меняет, кто узнаёт об авариях, — а такие изменения §10.4
    // требует записывать наравне с правами.
    NOTIFICATION_CHANNEL_CREATED: 'notification.channel_created',
    NOTIFICATION_CHANNEL_UPDATED: 'notification.channel_updated',
    NOTIFICATION_CHANNEL_DELETED: 'notification.channel_deleted',
    NOTIFICATION_RULE_CREATED: 'notification.rule_created',
    NOTIFICATION_RULE_UPDATED: 'notification.rule_updated',
    NOTIFICATION_RULE_DELETED: 'notification.rule_deleted',
    // Сканирование чужой подсети — действие, которое служба безопасности
    // заказчика видит со своей стороны. Запись о том, кто и что сканировал,
    // нужна ровно на этот случай.
    DISCOVERY_STARTED: 'discovery.started',
    // Обновление агента подменяет исполняемый файл на чужом сервере.
    // Кто и когда это затеял — вопрос, который задают после того, как
    // площадка не вернулась.
    AGENT_UPDATE_OFFERED: 'agent.update_offered',
    // Фаза 9b. Терминал — это выполнение команд на чужой машине; кто и когда
    // на неё входил, спрашивают ровно тогда же, когда про VNC. Содержимое
    // сессии не пишется намеренно (решение заказчика 29.08.2026): в потоке
    // терминала оказываются пароли, которые вводят в `su`.
    SSH_CONNECT: 'ssh.connect',
    SSH_DISCONNECT: 'ssh.disconnect',
    // Закрепление нового ключа хоста меняет, какую машину платформа считает
    // своей. Запись нужна ровно на случай, когда выяснится, что подтвердили
    // зря.
    SSH_HOST_KEY_TRUSTED: 'ssh.host_key_trusted',
};
export const auditEntrySchema = z.object({
    id: z.uuid(),
    action: z.string(),
    userId: z.uuid().nullable(),
    /** Отображаемое имя на момент чтения; для удалённых пользователей — null. */
    userDisplayName: z.string().nullable(),
    targetType: z.string().nullable(),
    targetId: z.uuid().nullable(),
    details: z.record(z.string(), z.unknown()),
    ip: z.string().nullable(),
    at: z.iso.datetime(),
});
export const auditQuerySchema = z.object({
    action: z.string().max(100).optional(),
    /**
     * Поиск по всем полям записи: действие, имя и адрес пользователя, объект,
     * IP и подробности.
     *
     * Подробности — JSON произвольного состава, и искать в них приходится
     * по тексту: путь к файлу, имя узла, причина отказа лежат именно там,
     * а колонки под них нет и быть не может.
     */
    q: z.string().trim().min(1).max(200).optional(),
    userId: z.uuid().optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().min(1).optional(),
});
/**
 * Страница журнала вместе с общим числом записей.
 *
 * Общее число не входит в обычную страницу (§8.1): курсорная пагинация
 * его не требует, а на растущих таблицах телеметрии считать его было бы
 * дорого без пользы. Журналу же оно нужно по существу — «сколько всего
 * событий за период» и «сколько нашлось по запросу» это и есть ответ,
 * за которым в журнал приходят.
 */
export const auditPageSchema = z.object({
    items: z.array(auditEntrySchema),
    nextCursor: z.string().nullable(),
    /** Сколько записей отвечает фильтру целиком, а не на этой странице. */
    total: z.number().int().nonnegative(),
});
//# sourceMappingURL=audit.js.map